import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";

const authHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function statusBadge(loan) {
  if (loan.status === "PENDING") {
    return <span className="badge badge-warning">Menunggu</span>;
  }
  if (loan.status === "REJECTED") {
    return <span className="badge badge-danger">Ditolak</span>;
  }
  if (loan.status === "RETURNED") {
    return <span className="badge badge-success">Dikembalikan</span>;
  }
  if (loan.status === "RETURNING") {
    return <span className="badge badge-warning">Minta Kembali</span>;
  }
  if (loan.is_overdue) {
    return <span className="badge badge-danger">Terlambat {Math.abs(loan.days_until_due)} hari</span>;
  }
  if (loan.days_until_due === 0) {
    return <span className="badge badge-danger">Jatuh tempo hari ini</span>;
  }
  if (loan.days_until_due === 1) {
    return <span className="badge badge-warning">Jatuh tempo besok</span>;
  }
  return <span className="badge badge-info">{loan.days_until_due} hari lagi</span>;
}

// Searchable item picker — type to filter, click to select
function ItemSearch({ items, value, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = items.find((it) => String(it.id) === String(value));

  const filtered = query.trim()
    ? items.filter((it) => {
        const q = query.toLowerCase();
        return (
          it.code.toLowerCase().includes(q) ||
          it.name.toLowerCase().includes(q)
        );
      })
    : items;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (it) => {
    onChange(String(it.id));
    setQuery("");
    setOpen(false);
  };

  const clear = () => {
    onChange("");
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      {/* Trigger / search input */}
      <div
        className="glass-input flex items-center gap-2 cursor-text"
        onClick={() => { setOpen(true); }}
      >
        {selected && !open ? (
          <>
            <span className="flex-1 text-sm text-strong truncate">
              <span className="font-mono text-brand-600 dark:text-brand-400 text-xs mr-1">{selected.code}</span>
              {selected.name}
              <span className="ml-1 text-xs text-muted">(stok: {selected.quantity_available})</span>
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); clear(); }}
              className="text-muted hover:text-strong flex-shrink-0 leading-none"
            >
              ×
            </button>
          </>
        ) : (
          <input
            autoFocus={open}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={selected ? `${selected.code} - ${selected.name}` : "Ketik kode atau nama barang…"}
            className="flex-1 bg-transparent outline-none text-sm text-strong placeholder:text-muted min-w-0"
          />
        )}
      </div>

      {/* Dropdown list */}
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 glass-dropdown rounded-xl max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-xs text-muted">Barang tidak ditemukan.</p>
          ) : (
            filtered.map((it) => (
              <button
                key={it.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(it)}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/40 dark:hover:bg-white/5 ${
                  String(it.id) === String(value) ? "bg-brand-500/10" : ""
                }`}
              >
                <span className="flex-1 min-w-0">
                  <span className="font-mono text-xs text-brand-600 dark:text-brand-400 mr-1.5">{it.code}</span>
                  <span className="text-strong truncate">{it.name}</span>
                </span>
                <span className="text-xs text-muted ml-2 flex-shrink-0">stok: {it.quantity_available}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function LoanTransactions() {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [creatorOptions, setCreatorOptions] = useState([]);

  // share link modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareToken, setShareToken] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState("");
  const [shareCopied, setShareCopied] = useState(false);
  const [shareRevoking, setShareRevoking] = useState(false);
  const [shareRevoked, setShareRevoked] = useState(false);

  // Create form
  const [showForm, setShowForm] = useState(false);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [formItemId, setFormItemId] = useState("");
  const [formLocationId, setFormLocationId] = useState("");
  const [formQty, setFormQty] = useState("");
  const [formPicName, setFormPicName] = useState("");
  const [formDueDate, setFormDueDate] = useState("");
  const [formNote, setFormNote] = useState("");
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  // items queued for this same submission (multi-item loan), each a
  // self-contained package: { item_id, quantity, location_id, location_name, label, note }
  const [lineItems, setLineItems] = useState([]);

  // Return confirm modal
  const [returnModal, setReturnModal] = useState(null);
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnError, setReturnError] = useState("");
  const [returnHasDamage, setReturnHasDamage] = useState(false);
  const [returnDamageQty, setReturnDamageQty] = useState("");
  const [returnDamageNote, setReturnDamageNote] = useState("");

  // Approve / reject modal
  const [approveModal, setApproveModal] = useState(null); // loan object
  const [approveAction, setApproveAction] = useState("approve"); // "approve" | "reject"
  const [approveReason, setApproveReason] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/auth/me/`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setUser)
      .catch(() => {});
  }, []);

  const fetchLoans = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (createdByFilter) params.set("created_by", createdByFilter);
    if (search.trim()) params.set("search", search.trim());

    fetch(`${API_BASE_URL}/api/loan-transactions/?${params}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        setLoans(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [statusFilter, createdByFilter, search]);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  // Prefetch items and locations for form
  useEffect(() => {
    if (!showForm) return;
    fetch(`${API_BASE_URL}/api/items/?page_size=500&status=active&temporary_only=false`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => setItems(data.results || []))
      .catch(() => {});
    fetch(`${API_BASE_URL}/api/locations/`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => setLocations(Array.isArray(data) ? data : data.results || []))
      .catch(() => {});
  }, [showForm]);

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  // Fetch an unfiltered snapshot once (admin/superadmin only) purely to build
  // the "Peminjam" filter dropdown — kept independent of the filtered `loans`
  // list so the options don't shrink as other filters are applied.
  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${API_BASE_URL}/api/loan-transactions/`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const seen = new Map();
        list.forEach((loan) => {
          if (loan.created_by && loan.created_by_username && !seen.has(loan.created_by)) {
            seen.set(loan.created_by, loan.created_by_username);
          }
        });
        setCreatorOptions(
          Array.from(seen, ([value, label]) => ({ value: String(value), label }))
        );
      })
      .catch(() => {});
  }, [isAdmin]);

  const handleShare = () => {
    setShowShareModal(true);
    setShareUrl("");
    setShareToken("");
    setShareError("");
    setShareCopied(false);
    setShareRevoked(false);
    setShareLoading(true);

    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (createdByFilter) params.set("created_by", createdByFilter);
    if (search.trim()) params.set("search", search.trim());

    fetch(`${API_BASE_URL}/api/loan-transactions/share-link/?${params}`, { headers: authHeaders() })
      .then((r) => {
        if (!r.ok) throw new Error("Gagal membuat link.");
        return r.json();
      })
      .then((data) => {
        setShareToken(data.token);
        setShareUrl(`${window.location.origin}/rekap/pinjaman?token=${encodeURIComponent(data.token)}`);
      })
      .catch(() => setShareError("Gagal membuat link share."))
      .finally(() => setShareLoading(false));
  };

  const handleRevokeShare = () => {
    if (!shareToken || shareRevoking) return;
    setShareRevoking(true);
    fetch(`${API_BASE_URL}/api/loan-transactions/share-link/revoke/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ token: shareToken }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("Gagal mencabut link.");
        return r.json();
      })
      .then(() => setShareRevoked(true))
      .catch(() => setShareError("Gagal mencabut link."))
      .finally(() => setShareRevoking(false));
  };

  // Snapshots the current entry form into a self-contained "package": its own
  // item, quantity, location, PIC, due date and note — none of it shared
  // with other queued packages.
  const buildLineFromCurrentEntry = (itemId, qtyNumber) => {
    const item = items.find((it) => it.id === itemId);
    const location = locations.find((l) => String(l.id) === String(formLocationId));
    return {
      item_id: itemId,
      quantity: qtyNumber,
      location_id: Number(formLocationId),
      location_name: location?.name || "-",
      label: item ? `${item.code} — ${item.name}` : `#${itemId}`,
      pic_name: formPicName.trim(),
      due_date: formDueDate,
      note: formNote.trim(),
    };
  };

  const validateCurrentEntry = () => {
    if (!formLocationId) return "Pilih gudang asal terlebih dahulu.";
    if (!formPicName.trim()) return "Nama PIC / Peminjam wajib diisi.";
    if (!formDueDate) return "Tanggal jatuh tempo wajib diisi.";
    return null;
  };

  // queue the currently-entered item as one more self-contained package
  // (own item, qty, location, PIC, due date, note) in this same submission
  const handleAddLineItem = () => {
    setFormError("");
    if (!formItemId) {
      setFormError("Pilih barang terlebih dahulu sebelum menambahkan ke daftar.");
      return;
    }
    const qtyNumber = Number(formQty);
    if (!qtyNumber || qtyNumber <= 0) {
      setFormError("Jumlah harus lebih dari 0.");
      return;
    }
    const entryError = validateCurrentEntry();
    if (entryError) {
      setFormError(entryError);
      return;
    }
    const itemId = Number(formItemId);
    if (lineItems.some((li) => li.item_id === itemId)) {
      setFormError("Barang sudah ada di daftar. Hapus dulu jika ingin mengubah qty.");
      return;
    }

    setLineItems((prev) => [...prev, buildLineFromCurrentEntry(itemId, qtyNumber)]);

    // clear EVERY field, including gudang/PIC/due date — nothing carries
    // over silently, each package must be filled in on its own so a value
    // left over from the previous item can never leak into this one
    setFormItemId("");
    setFormLocationId("");
    setFormQty("");
    setFormPicName("");
    setFormDueDate("");
    setFormNote("");
  };

  const handleRemoveLineItem = (itemId) => {
    setLineItems((prev) => prev.filter((li) => li.item_id !== itemId));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");

    // fold whatever is currently sitting in the entry fields into the list
    // of packages, alongside anything already queued via "+ Item" — each
    // package carries its own PIC/due date/note, not shared with the others.
    let allItems = [...lineItems];
    if (formItemId) {
      const qtyNumber = Number(formQty);
      if (!qtyNumber || qtyNumber <= 0) {
        setFormError("Jumlah harus lebih dari 0.");
        return;
      }
      const entryError = validateCurrentEntry();
      if (entryError) {
        setFormError(entryError);
        return;
      }
      const itemId = Number(formItemId);
      if (allItems.some((li) => li.item_id === itemId)) {
        setFormError("Barang sudah ada di daftar. Hapus salah satu sebelum submit.");
        return;
      }
      allItems.push(buildLineFromCurrentEntry(itemId, qtyNumber));
    }

    if (allItems.length === 0) {
      setFormError("Semua field wajib diisi.");
      return;
    }

    setFormLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/loan-transactions/`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          items: allItems.map((li) => ({
            item_id: li.item_id,
            location_id: li.location_id,
            quantity: li.quantity,
            pic_name: li.pic_name,
            due_date: li.due_date,
            note: li.note,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.quantity?.[0] || data?.detail || JSON.stringify(data);
        throw new Error(msg);
      }
      setShowForm(false);
      setFormItemId(""); setFormLocationId(""); setFormQty("");
      setFormPicName(""); setFormDueDate(""); setFormNote("");
      setLineItems([]);
      fetchLoans();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleReturn = async () => {
    if (!returnModal) return;
    setReturnLoading(true);
    setReturnError("");

    const damageQtyNum = returnHasDamage ? Number(returnDamageQty) : 0;
    if (returnHasDamage) {
      if (!returnDamageQty || damageQtyNum < 1) {
        setReturnError("Jumlah kerusakan harus diisi dan lebih dari 0.");
        setReturnLoading(false);
        return;
      }
      if (damageQtyNum > returnModal.quantity) {
        setReturnError(`Jumlah kerusakan tidak boleh melebihi jumlah pinjaman (${returnModal.quantity}).`);
        setReturnLoading(false);
        return;
      }
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/loan-transactions/${returnModal.id}/return/`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          damage_qty: damageQtyNum,
          damage_note: returnHasDamage ? returnDamageNote.trim() : "",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || data?.damage_qty?.[0] || "Gagal memproses pengembalian.");
      setReturnModal(null);
      setReturnHasDamage(false);
      setReturnDamageQty("");
      setReturnDamageNote("");
      fetchLoans();
    } catch (err) {
      setReturnError(err.message);
    } finally {
      setReturnLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approveModal) return;
    setApproveLoading(true);
    setApproveError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/loan-transactions/${approveModal.id}/approve/`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ action: approveAction, rejected_reason: approveReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.quantity?.[0] || data?.detail || JSON.stringify(data));
      setApproveModal(null);
      setApproveReason("");
      fetchLoans();
    } catch (err) {
      setApproveError(err.message);
    } finally {
      setApproveLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-strong">Peminjaman Barang</h1>
          <p className="text-sm text-muted mt-0.5">Catat dan kelola transaksi peminjaman dengan jatuh tempo</p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => { setShowForm((v) => !v); setFormError(""); }}
            className="btn-primary"
          >
            {showForm ? "Batal" : "+ Buat Peminjaman"}
          </button>
        )}
      </div>

      {/* Create Form */}
      {showForm && isAdmin && (
        <div className="glass-panel p-5 rounded-2xl">
          <h2 className="text-base font-semibold text-strong mb-4">Form Peminjaman Baru</h2>
          <p className="text-xs text-muted -mt-3 mb-4">
            Tiap kali klik <b>+ Item</b>, barang itu tersimpan sebagai bungkusan sendiri lalu semua field di bawah ini
            (termasuk Gudang, PIC, Jatuh Tempo, Catatan) dikosongkan lagi — isi ulang untuk barang berikutnya.
          </p>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Barang *</label>
              <ItemSearch items={items} value={formItemId} onChange={setFormItemId} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Gudang Asal *</label>
              <CustomSelect
                value={formLocationId}
                onChange={setFormLocationId}
                placeholder="Pilih gudang..."
                options={locations.map((loc) => ({ value: String(loc.id), label: loc.name }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Jumlah *</label>
              <div className="flex gap-2">
                <input type="number" min="1" value={formQty} onChange={(e) => setFormQty(e.target.value)} className="glass-input" placeholder="e.g. 2" />
                <button
                  type="button"
                  onClick={handleAddLineItem}
                  title="Simpan sebagai bungkusan sendiri, lalu isi ulang semua field (gudang/PIC/jatuh tempo/note) untuk barang berikutnya"
                  className="shrink-0 inline-flex items-center justify-center rounded-md border border-brand-500/50 text-brand-600 text-sm font-semibold px-3 py-2 hover:bg-brand-500/10"
                >
                  + Item
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Catatan</label>
              <input type="text" value={formNote} onChange={(e) => setFormNote(e.target.value)} className="glass-input" placeholder="Optional" />
            </div>

            {lineItems.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Barang dalam peminjaman ini ({lineItems.length})
                </label>
                <div className="flex flex-wrap gap-2">
                  {lineItems.map((li) => (
                    <span
                      key={li.item_id}
                      className="inline-flex items-center gap-2 rounded-full border border-white/40 dark:border-white/15 bg-white/60 dark:bg-white/5 px-3 py-1 text-xs"
                      title={`PIC: ${li.pic_name} | Jatuh tempo: ${li.due_date}${li.note ? ` | ${li.note}` : ""}`}
                    >
                      {li.label} × {li.quantity} @ {li.location_name} — {li.pic_name} (jatuh tempo {li.due_date})
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(li.item_id)}
                        className="text-red-500 hover:text-red-700 font-bold leading-none"
                        title="Hapus barang ini"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Nama PIC / Peminjam *</label>
              <input type="text" value={formPicName} onChange={(e) => setFormPicName(e.target.value)} className="glass-input" placeholder="e.g. Budi - HRD" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Tanggal Jatuh Tempo *</label>
              <input type="date" value={formDueDate} onChange={(e) => setFormDueDate(e.target.value)} className="glass-input" />
            </div>
            {formError && (
              <div className="md:col-span-2 text-sm text-red-500">{formError}</div>
            )}
            <div className="md:col-span-2 flex justify-end">
              <button type="submit" disabled={formLoading} className="btn-primary">
                {formLoading ? "Menyimpan..." : "Simpan Peminjaman"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="glass-input"
            placeholder="Cari referensi, barang, PIC..."
          />
        </div>
        <CustomSelect
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Semua Status"
          className="w-48"
          options={[
            { value: "PENDING",   label: "Menunggu"           },
            { value: "ACTIVE",    label: "Aktif"              },
            { value: "RETURNING", label: "Minta Kembali"      },
            { value: "RETURNED",  label: "Dikembalikan"       },
            { value: "REJECTED",  label: "Ditolak"            },
            { value: "OVERDUE",   label: "Terlambat"          },
          ]}
        />
        {isAdmin && (
          <CustomSelect
            value={createdByFilter}
            onChange={setCreatedByFilter}
            placeholder="Semua Peminjam"
            className="w-48"
            options={creatorOptions}
          />
        )}
        <button
          type="button"
          onClick={handleShare}
          className="btn-ghost !px-3 !py-1.5 !text-xs"
        >
          Share
        </button>
      </div>

      {/* Table */}
      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="text-left">Referensi</th>
              <th className="text-left">Barang</th>
              <th className="text-left">Gudang</th>
              <th className="!text-right">Qty</th>
              <th className="text-left">PIC</th>
              <th className="text-left">Jatuh Tempo</th>
              <th className="text-left">Status</th>
              <th className="text-left">Pengaju</th>
              <th className="text-left">Tgl Dibuat</th>
              {isAdmin && <th className="text-left">Aksi</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isAdmin ? 10 : 9} className="text-center py-8 text-muted">
                  Loading...
                </td>
              </tr>
            ) : loans.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 10 : 9} className="text-center py-8 text-muted">
                  Tidak ada data peminjaman.
                </td>
              </tr>
            ) : (
              [...loans]
                .sort((a, b) => {
                  const p = { PENDING: 0, RETURNING: 1 };
                  const pa = p[a.status] ?? 2;
                  const pb = p[b.status] ?? 2;
                  return pa - pb;
                })
                .map((loan) => (
                <tr key={loan.id} className={loan.is_overdue && loan.status === "ACTIVE" ? "bg-red-500/5" : ""}>
                  <td className="font-mono text-xs">{loan.reference_code}</td>
                  <td>
                    <div className="font-medium text-strong">{loan.item_name}</div>
                    <div className="text-xs text-muted">{loan.item_code}</div>
                  </td>
                  <td className="text-sm">{loan.location_name}</td>
                  <td className="text-right font-mono">{loan.quantity} {loan.item_unit}</td>
                  <td className="text-sm">{loan.pic_name}</td>
                  <td className="text-sm font-mono">{loan.due_date}</td>
                  <td>{statusBadge(loan)}</td>
                  <td className="text-xs text-muted">{loan.created_by_username || "-"}</td>
                  <td className="text-xs text-muted">
                    {new Date(loan.created_at).toLocaleDateString("id-ID")}
                  </td>
                  {isAdmin && (
                    <td>
                      {loan.status === "PENDING" && (
                        <div className="flex gap-1.5">
                          <button type="button" onClick={() => { setApproveModal(loan); setApproveAction("approve"); setApproveReason(""); setApproveError(""); }}
                            className="btn-sm btn-primary">Setuju</button>
                          <button type="button" onClick={() => { setApproveModal(loan); setApproveAction("reject"); setApproveReason(""); setApproveError(""); }}
                            className="btn-sm btn-danger">Tolak</button>
                        </div>
                      )}
                      {loan.status === "RETURNING" && (
                        <button type="button" onClick={() => { setReturnModal(loan); setReturnHasDamage(false); setReturnDamageQty(""); setReturnDamageNote(""); setReturnError(""); }} className="btn-sm btn-primary">
                          Konfirmasi Kembali
                        </button>
                      )}
                      {loan.status === "ACTIVE" && (
                        <button type="button" onClick={() => { setReturnModal(loan); setReturnHasDamage(false); setReturnDamageQty(""); setReturnDamageNote(""); setReturnError(""); }} className="btn-sm btn-outline">
                          Kembalikan
                        </button>
                      )}
                      {loan.status === "RETURNED" && (
                        <div className="text-xs text-muted">
                          <div>{loan.returned_at ? new Date(loan.returned_at).toLocaleDateString("id-ID") : "-"}</div>
                          {loan.damage_qty > 0 && (
                            <div className="text-red-400 font-medium mt-0.5" title={loan.damage_note || undefined}>
                              {loan.damage_qty} rusak
                            </div>
                          )}
                        </div>
                      )}
                      {loan.status === "REJECTED" && loan.rejected_reason && (
                        <span className="text-xs text-muted" title={loan.rejected_reason}>
                          {loan.rejected_reason.length > 30 ? loan.rejected_reason.slice(0, 30) + "…" : loan.rejected_reason}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Approve / Reject Modal */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="glass-panel rounded-2xl p-6 w-full max-w-md mx-4 shadow-glass">
            <h3 className="text-base font-semibold text-strong mb-1">
              {approveAction === "approve" ? "Setujui Pengajuan" : "Tolak Pengajuan"}
            </h3>
            <p className="text-sm text-muted mb-4">
              <strong>{approveModal.reference_code}</strong> — {approveModal.item_name} x{approveModal.quantity}
              {" "}| Oleh: <strong>{approveModal.created_by_username}</strong>
            </p>

            {approveAction === "reject" && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-muted mb-1.5">Alasan Penolakan</label>
                <input type="text" value={approveReason} onChange={(e) => setApproveReason(e.target.value)}
                  className="glass-input" placeholder="Opsional — alasan penolakan..." />
              </div>
            )}

            {approveError && <p className="text-sm mb-3" style={{ color: "var(--danger)" }}>{approveError}</p>}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setApproveModal(null)} className="btn-outline" disabled={approveLoading}>
                Batal
              </button>
              <button type="button" onClick={handleApprove} disabled={approveLoading}
                className={approveAction === "approve" ? "btn-primary" : "btn-danger"}>
                {approveLoading ? "Memproses..." : approveAction === "approve" ? "Ya, Setujui" : "Ya, Tolak"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Confirm Modal */}
      {returnModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="glass-panel rounded-2xl p-6 w-full max-w-md mx-4 shadow-glass my-auto">
            <h3 className="text-base font-semibold text-strong mb-2">
              {returnModal.status === "RETURNING" ? "Konfirmasi Pengembalian" : "Proses Pengembalian"}
            </h3>
            <p className="text-sm text-muted mb-1">
              <strong>{returnModal.reference_code}</strong> — {returnModal.item_name} x{returnModal.quantity}
            </p>
            <p className="text-sm text-muted mb-4">
              PIC: <strong>{returnModal.pic_name}</strong> | Jatuh Tempo: <strong>{returnModal.due_date}</strong>
            </p>

            {/* Foto pengembalian dari user */}
            {returnModal.return_photo_url && (
              <div className="mb-4">
                <p className="text-xs font-medium text-muted mb-2">Foto Pengembalian dari Peminjam:</p>
                {returnModal.return_note && (
                  <p className="text-xs text-muted mb-2 italic">"{returnModal.return_note}"</p>
                )}
                <a href={returnModal.return_photo_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={returnModal.return_photo_url}
                    alt="Foto pengembalian"
                    className="w-full max-h-48 object-contain rounded-xl border border-white/20 bg-black/10 cursor-zoom-in"
                  />
                </a>
                <p className="text-[11px] text-muted mt-1">Klik foto untuk buka penuh</p>
              </div>
            )}

            {/* Damage option */}
            <div className="border border-gray-600/50 rounded-xl p-4 mb-4 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={returnHasDamage}
                  onChange={(e) => {
                    setReturnHasDamage(e.target.checked);
                    if (!e.target.checked) { setReturnDamageQty(""); setReturnDamageNote(""); }
                  }}
                  className="w-4 h-4 accent-red-500"
                />
                <span className="text-sm font-medium text-strong">Ada barang rusak?</span>
              </label>

              {returnHasDamage && (
                <div className="space-y-2 pt-1">
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">
                      Jumlah Rusak <span className="text-red-400">*</span>
                      <span className="ml-1 text-muted">(maks {returnModal.quantity})</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={returnModal.quantity}
                      value={returnDamageQty}
                      onChange={(e) => setReturnDamageQty(e.target.value)}
                      className="glass-input"
                      placeholder="e.g. 1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">Keterangan Kerusakan</label>
                    <input
                      type="text"
                      value={returnDamageNote}
                      onChange={(e) => setReturnDamageNote(e.target.value)}
                      className="glass-input"
                      placeholder="Deskripsi kerusakan..."
                    />
                  </div>
                  {returnDamageQty && Number(returnDamageQty) < returnModal.quantity && (
                    <p className="text-xs text-amber-400">
                      {returnModal.quantity - Number(returnDamageQty)} unit kembali ke stok tersedia,{" "}
                      {returnDamageQty} unit masuk stok rusak.
                    </p>
                  )}
                  {returnDamageQty && Number(returnDamageQty) === returnModal.quantity && (
                    <p className="text-xs text-red-400">
                      Semua {returnModal.quantity} unit akan masuk stok rusak.
                    </p>
                  )}
                </div>
              )}
            </div>

            {returnError && <p className="text-sm text-red-500 mb-3">{returnError}</p>}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setReturnModal(null);
                  setReturnHasDamage(false);
                  setReturnDamageQty("");
                  setReturnDamageNote("");
                  setReturnError("");
                }}
                className="btn-outline"
                disabled={returnLoading}
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleReturn}
                disabled={returnLoading}
                className="btn-primary"
              >
                {returnLoading ? "Memproses..." : "Ya, Kembalikan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share link modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="glass-card w-full max-w-md p-6 animate-scale-in">
            <h2 className="text-sm font-semibold text-strong mb-3">Share Daftar Peminjaman</h2>
            <p className="text-xs text-muted mb-3">
              Link ini menampilkan daftar peminjaman sesuai filter yang sedang aktif (status, peminjam, pencarian) — tanpa perlu login.
            </p>

            {shareLoading && <p className="text-xs text-muted py-2">Membuat link…</p>}
            {!shareLoading && shareError && <p className="text-xs text-red-500 py-2">{shareError}</p>}

            {!shareLoading && shareUrl && !shareRevoked && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    onFocus={(e) => e.target.select()}
                    className="glass-input flex-1 text-xs"
                  />
                  <button
                    type="button"
                    className="btn-primary !px-3 !py-1.5 !text-xs shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl);
                      setShareCopied(true);
                    }}
                  >
                    {shareCopied ? "Tersalin" : "Salin"}
                  </button>
                </div>
                <button
                  type="button"
                  className="text-xs text-red-500 hover:text-red-700"
                  onClick={handleRevokeShare}
                  disabled={shareRevoking}
                >
                  {shareRevoking ? "Mencabut…" : "Cabut link ini"}
                </button>
              </>
            )}

            {!shareLoading && shareRevoked && (
              <p className="text-xs text-green-600 py-2">
                Link ini sudah dicabut dan tidak bisa diakses lagi.
              </p>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="btn-ghost !px-3 !py-1.5 !text-xs"
                onClick={() => setShowShareModal(false)}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoanTransactions;
